import { computed, defineComponent, h } from 'vue'

import { QSelect, QItem, QItemSection, QItemLabel } from 'quasar'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
// Utils
import { slot } from '../utils'
import { quasarHandlers } from '../quasarHandlers'
import { useTemplateRef } from '../useTemplateRef'

/**
 * A select that behaves like a menu of buttons: picking an option is an action, so each option emits
 * `clickItem` (or calls its own `onClick`) and stops QSelect handling the click itself.
 *
 * Option value `-1` is the "Please select..." placeholder — it is not rendered as an option, and
 * `isComponentValid` rejects it, which is why this overrides the default emptiness check.
 */
export default defineComponent({
  name: 'BsSelectBtn',

  // See BsInput: undeclared DOM-named events are bound twice. `clickBtn`/`clickItem` are this
  // component's own; they are declared so a caller's handlers are not also native root listeners.
  emits: [ 'input', 'update:modelValue', 'clickBtn', 'clickItem' ],

  props: {
    ...bsComponentProps,
    dense: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    // Template refs. A string `ref` in a render function does not populate `$refs` for a
    // component whose logic is in setup() -- see useTemplateRef.
    const { ref: bsSelectBtn, set: setBsSelectBtnRef } = useTemplateRef<any>()

    const vm = useVm()
    const bs = useBsComponent({
      vm,
      props,
      emit,
      autoSaveMethod: 'input',
      // Override this so we can allow for negative number options to be something like "Please select..."
      isComponentValid: (val?: any) => val !== null && val !== '' && val !== void 0 && val > -1
    })

    const backgroundColor = computed(() => {
      if (bs.hasErrors.value) {
        return 'imp-bs-r-l'
      }

      return vm.$attrs['bg-color'] || 'white'
    })

    return {
      bsSelectBtn,
      setBsSelectBtnRef,
      ...bs,
      backgroundColor
    }
  },

  methods: {
    /** Renamed from `__renderOptions`: Vue does not proxy setup/method keys beginning with `_`. */
    renderOptions (props: Record<string, any>) {
      // We don't want to show the default option as a selectable option as these are essentially buttons.
      if (props.opt.value === -1) return

      return h(QItem, {
        class: 'bs-select-btn-option',
        // Quasar 2's option scope is one `itemProps` bag — `clickable`, `active`, the aria attributes
        // and `onClick`. Quasar 1 split it into `props` and `itemEvents`; those names find nothing here
        // now, and an option that renders but cannot be clicked is the result.
        ...props.itemProps
      // A component's children have to be a slot function in Vue 3; an array warns and is
      // treated as a default slot that cannot be forwarded.
      }, { default: () => [
        h(QItemSection, {
          onClick: (e: MouseEvent) => {
              if (typeof props.opt.onClick === 'function') {
                props.opt.onClick(props.opt.value)
              }
              this.$emit('clickItem', props.opt.value)
              // Stop the default QSelect clicks
              e.stopPropagation()
              e.preventDefault()
            }
        }, { default: () => [
          // Slots are h()'s THIRD argument. As a prop, `default` is an unknown prop on QItemLabel and
          // the label renders empty -- every option in the menu came out blank.
          h(QItemLabel, null, { default: () => [props.opt.label] })
        ] })
      ] })
    },

    renderSelectedItem (props: Record<string, any>) {
      return h(QItem, {
        class: 'bs-selected-option',
        // The `selected-item` scope carries no props or handlers in Quasar 2 (nor in Quasar 1), so the
        // `props`/`itemEvents` spreads this had were always no-ops. Its own click handler is on the
        // section below.
        clickable: false,
        // Don't use this - we'll use this .sass style instead
        dense: true
      // A component's children have to be a slot function in Vue 3; an array warns and is
      // treated as a default slot that cannot be forwarded.
      }, { default: () => [
        h(QItemSection, {
          onClick: (e: MouseEvent) => {
              if (typeof props.opt.onClick === 'function') {
                props.opt.onClick(props.opt.value)
              }
              this.$emit('clickBtn', props.opt.value)
              // Stop the default QSelect clicks
              e.stopPropagation()
              e.preventDefault()
            }
        }, { default: () => [
          // As above: the slot is h()'s third argument, not a prop.
          h(QItemLabel, null, { default: () => [props.opt.label] })
        ] })
      ] })
    }
  },

  render () {
    const props = {
      mapOptions: this.$attrs['map-options'] !== void 0 ? this.$attrs['map-options'] : true,
      emitValue: this.$attrs['emit-value'] !== void 0 ? this.$attrs['emit-value'] : true,
      dropdownIcon: 'app:arrow-down',
      popupContentClass: 'bs-select-btn-options',
      stackLabel: true,
      borderless: true,
      // See BsInput: `getAttributes` passing `error: null` when clear takes QField's bottom row away,
      // and with it the auto-save hint. These are the only two field components that had been getting
      // the row from `error: false` rather than asking for it.
      bottomSlots: true,
      noErrorIcon: true,
      filled: !this.hasErrors,
      outlined: this.hasErrors,
      bgColor: this.backgroundColor
    }

    let scopedSlots: object = {
      ['selected-item']: (props: Record<string, any>) => this.renderSelectedItem(props)
    }
    // Only if we're not trying to override from outside the component
    if (this.$slots.option === void 0) {
      scopedSlots = {
        ...scopedSlots,
        option: (props: Record<string, any>) => this.renderOptions(props)
      }
    }

    return h(QSelect, {
      class: ['bs-select-btn', this.getClasses({
        'bs-select-btn--dense': this.dense
      })],
      ref: this.setBsSelectBtnRef,
      ...this.getRenderProps(props),
      ...quasarHandlers(this.getListeners({
        // `componentValue`, not `$attrs.value`: see BsSelect — a `v-model` caller sends `modelValue`,
        // and validating the missing name marks a filled control invalid.
        blur: () => this.validate(this.componentValue)
      })),
      ...this.getAttributes()
    }, {
      ...this.getSlots(scopedSlots),
      // Vue 3 takes ONE slots object; what this passed as a fourth argument is the default slot.
      default: () => [slot(this, 'default')]
    })
  }
})
